import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { Modal } from "@beleef/ui";
import {
  useAgencyPartiesQuery,
  useAddAgencyPartyMutation,
  useUpdateAgencyPartyMutation,
  useDeleteAgencyPartyMutation,
} from "apis/agencyParty";
import { FaPlus } from "react-icons/fa6";
import AgencyPartyEdit from "components/agencyParty/Edit";
import AgencyPartyView from "components/agencyParty/View";
import Table from "components/ui/Table";

const AgencyParty = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useAgencyPartiesQuery();

  const { mutate: createAgencyParty, isLoading: isCreateLoading } =
    useAddAgencyPartyMutation();
  const { mutate: updateAgencyParty } = useUpdateAgencyPartyMutation();
  const { mutate: deleteAgencyParty, isLoading: isDeleteLoading } =
    useDeleteAgencyPartyMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "company",
        header: "Company",
        isFilter: true,
        minWidth: "200px",
        cell: (row) => (
          <span className="text-sm font-medium">{row.company}</span>
        ),
      },
      {
        accessorKey: "abn",
        header: "ABN",
        isFilter: true,
        cell: (row) => (
          <span className="text-sm font-mono">{row.abn || "-"}</span>
        ),
      },
      {
        accessorKey: "licenceNumber",
        header: "Licence No.",
        isFilter: true,
        cell: (row) => (
          <span className="text-sm font-mono">{row.licenceNumber || "-"}</span>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        isFilter: true,
        minWidth: "180px",
        cell: (row) => <span className="text-sm">{row.email || "-"}</span>,
      },
      {
        accessorKey: "phone",
        header: "Phone",
        isFilter: false,
        cell: (row) => <span className="text-sm">{row.phone || "-"}</span>,
      },
      {
        accessorKey: "gst",
        header: "GST",
        isFilter: false,
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              row.gst === "Yes"
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {row.gst === "Yes" ? "Registered" : "Not Registered"}
          </span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        isFilter: false,
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString("en-AU", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Agency Party"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Agency Party"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  if (isDataPending) return <PageLoader />;

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading agency parties: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedAgencyParty = data?.find((ap) => ap._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateAgencyParty({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createAgencyParty(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteAgencyParty(selectedId);
    setIsDeleteOpen(false);
  };

  const handleRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Agency Party"
        className="max-w-3xl"
      >
        <AgencyPartyEdit
          initialData={selectedAgencyParty}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Agency Party"
        className="max-w-3xl"
      >
        <AgencyPartyEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Agency Party"
      >
        <div>
          <p className="mb-4">
            Are you sure you want to delete "{selectedAgencyParty?.company}"?
            This action cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isDeleteLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Agency Party Details"
        className="max-w-3xl"
      >
        <AgencyPartyView agencyParty={selectedAgencyParty} />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Agency Parties"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddButton}
          onRowClick={handleRowClick}
        />
      </div>
    </>
  );
};

export default AgencyParty;
